import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import mixpanel from "mixpanel-browser";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Badge, Button, Card, Dropdown, Grid, Icon, Spinner, Text } from "~/components-shared";
import { AuthoritiesEnum, CRUDActionEnum, IntegrationTypeEnum, InsightsReportScheduleTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import lists from "~/lib/lists";
import { resolveMonthDays } from "~/lib/helpers";
import toastsActionCreators from "~/store/toasts/actions";
import integrationSelectors from "~/store/integrations/selectors";
import insightsSelectors from "~/store/insights/selectors";
import insightsActionCreators from "~/store/insights/actions";
import useAbacExt from "~/hooks/abac";
import { InsightsActionEnum } from "~/store/insights/types";
const StyledGrid = styled(Grid)`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
`;
const InsightsInfoCard = _ref2 => {
  let {
    config,
    report,
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const configId = config.id || "";
  const reportId = report?.id || "";
  const historyReports = useSelector(insightsSelectors.getReportHistory(configId));
  const sendStatus = useSelector(insightsSelectors.getStatusBySendReport(reportId));
  const sendError = useSelector(insightsSelectors.getErrorBySendReport(reportId));
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const error = sendError;
  const {
    userHas
  } = useAbacExt();
  const handleEdit = id => {
    dispatch(insightsActionCreators.actionChanged(CRUDActionEnum.UPDATE, id));
    mixpanel.track(`Member Insight Config – Edit`, {
      configId: id
    });
  };
  const handleDelete = id => {
    dispatch(insightsActionCreators.actionChanged(CRUDActionEnum.DELETE, id));
    mixpanel.track(`Member Insight Config – Delete`, {
      configId: id
    });
  };
  const handleSend = callback => {
    if (reportId) {
      dispatch(insightsActionCreators.sendSlackReportStarted(configId, reportId, _ref3 => {
        let {
          data
        } = _ref3;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("InsightsInfoCard.reportSent"),
            timeout: 10000
          }));
          callback();
        }
      }));
      mixpanel.track(`Member Insights Report – Send in Slack`, {
        reportId
      });
    }
  };
  const handleClickReport = id => {
    history.push(id ? RoutesEnum.MEMBER_INSIGHTS_REPORT.replace(":configId", configId).replace(":reportId", id) : RoutesEnum.MEMBER_INSIGHTS_CONFIG.replace(":configId", configId));
    mixpanel.track(`Member Insights Report – Select`, {
      configId,
      reportId
    });
  };
  const handleShareInSlack = () => {
    dispatch(insightsActionCreators.actionChanged(InsightsActionEnum.SHARE_REPORT_IN_SLACK, configId, reportId));
    mixpanel.track(`Member Insights Report – Share in Slack`, {
      configId,
      reportId
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Card, _extends({
    color: "transparent",
    border: true
  }, rest), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 8,
    gap: 3,
    template: slackIntegration ? "auto auto auto auto 100px 2.7rem" : "auto auto auto auto 2.7rem",
    s: {
      pl: 6,
      pr: 4,
      py: 3
    },
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1
    }
  }, t("InsightsInfoCard.period")), report ? /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement(Button, {
      color: "translucent",
      s: {
        py: 1.5,
        pl: 4,
        pr: 2,
        ml: -3
      },
      onClick: toggle,
      outline: false
    }, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      weight: "bold",
      fontSize: 4
    }, resolveMonthDays(report.dateFrom, report.dateTo, true), /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      s: {
        ml: 1
      },
      rotate: isOpen ? 0 : 180
    })))
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "230px"
    }, historyReports.map((item, index) => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      key: item.id,
      onClick: () => {
        handleClickReport(index === 0 ? undefined : item.id);
        onItemClick();
      },
      selected: item.id === reportId
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold"
    }, resolveMonthDays(item.dateFrom, item.dateTo, true)), !!item.generatedAt && /*#__PURE__*/React.createElement(Badge, {
      fontSize: 3,
      color: "grey",
      s: {
        py: 0.5
      },
      rounded: true,
      style: {
        minWidth: "40px",
        textAlign: "center"
      }
    }, moment(item.generatedAt).format("ddd"))))));
  }) : /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 4,
    s: {
      mt: 1.5
    }
  }, "Not delivered")), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("InsightsInfoCard.scheduled")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 4
  }, lists.insights.scheduleTypes.names[config.scheduleType], config.scheduleType === InsightsReportScheduleTypeEnum.WEEKLY ? ` on ${moment().weekday(config.scheduleDayOfWeek - 1).format("ddd")}` : "", " ", "at ", moment.utc(config?.scheduleTimeOfDay, "hh:mma").local().format("hh:mma"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("InsightsInfoCard.deliveryDate")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, report?.generatedAt ? moment(report?.generatedAt).format("ddd, MMM Do [at] hh:mma") : "Not delivered yet")), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("InsightsInfoCard.topics")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, (report || config).topics.length || "All", " Followed Topics")), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("InsightsInfoCard.destination")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 4
  }, lists.insights.scheduleDestinations.names[config.scheduleDestination])), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    }),
    fullWidth: true
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHas([AuthoritiesEnum.MEMBER_INSIGHTS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleEdit(config.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "pencil",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), slackIntegration && report && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSend(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.send"), " to", " ", lists.insights.scheduleDestinations.names[config.scheduleDestination]), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "share",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TrendsInfoCard.shareInSlack"))), userHas([AuthoritiesEnum.MEMBER_INSIGHTS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete(config.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  })))));
};
export default InsightsInfoCard;